import React, { useState, useMemo, useEffect } from 'react';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Tooltip, IconButton, Switch, FormControlLabel, Autocomplete, TextField, CircularProgress, Skeleton } from '@mui/material';
import { ShiftTemplate, RecurrenceType, TemplateStatus } from '../types';
import { formatTime, formatDateTime } from '../utils';
import { cityOptions } from '../../../constants/enums';
import CustomButton from '../../../components/common/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Badge } from '../../../pages/ShiftManagement/components/ui/Badge';
import { TemplateCard } from './TemplateCard';

interface ShiftTemplatesViewProps {
  templates: ShiftTemplate[];
  onCreateTemplate: () => void;
  onViewTemplate: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  onDeleteTemplate: (templateId: string) => void;
  onToggleTemplate: (templateId: string) => void;
  onGenerateShifts: (templateId: string) => void;
  viewMode?: 'card' | 'table';
  onViewModeChange?: (mode: 'card' | 'table') => void;
  page?: number;
  totalPages?: number;
  totalTemplates?: number;
  onPageChange?: (page: number) => void;
  ITEMS_PER_PAGE?: number;
  loading?: boolean;
  filters: {
    cityId?: number | undefined;
    status?: string | undefined;
    recurrenceType?: string | undefined;
    search?: string | undefined;
  };
  onFiltersChange: (filters: {
    cityId?: number | undefined;
    status?: string | undefined;
    recurrenceType?: string | undefined;
    search?: string | undefined;
  }) => void;
}

const DEFAULT_ITEMS_PER_PAGE = 12;

export const ShiftTemplatesView: React.FC<ShiftTemplatesViewProps> = ({
  templates,
  onCreateTemplate,
  onViewTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onToggleTemplate,
  onGenerateShifts,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  page: externalPage,
  totalPages: externalTotalPages,
  totalTemplates: externalTotalTemplates,
  onPageChange: externalOnPageChange,
  ITEMS_PER_PAGE = DEFAULT_ITEMS_PER_PAGE,
  loading = false,
  filters,
  onFiltersChange,
}) => {
  const [internalViewMode, setInternalViewMode] = useState<'card' | 'table'>('card');
  const [internalPage, setInternalPage] = useState(0);

  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = externalOnViewModeChange ?? setInternalViewMode;
  const page = externalPage ?? internalPage;
  const setPage = externalOnPageChange ?? setInternalPage;

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const currentSearch = filters.search || '';
    if (debouncedSearch !== currentSearch) {
      onFiltersChange({ ...filters, search: debouncedSearch || undefined });
      setPage(0);
    }
  }, [debouncedSearch]);

  const displayedTemplates = templates;

  const hasActiveFilters = !!(filters.status || filters.cityId || filters.recurrenceType || filters.search);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
    setPage(0);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      cityId: undefined,
      status: undefined,
      recurrenceType: undefined,
      search: undefined,
    });
    setPage(0);
  };

  const getCityName = (cityId?: string | number) => {
    if (!cityId) return 'All Cities';
    const city = cityOptions.find(c => String(c.id) === String(cityId));
    return city?.name || String(cityId);
  };

  const getNeighborhoodName = (neighborhoodId?: string) => {
    if (!neighborhoodId) return 'N/A';
    return neighborhoodId;
  };

  const getRecurrenceLabel = (type: RecurrenceType, template: ShiftTemplate): string => {
    switch (type) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'specific_date':
        return 'Specific Date';
      case 'custom_range':
        return 'Custom Range';
      default:
        return type;
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeVariant = (status: TemplateStatus): 'success' | 'secondary' => {
    return status === 'active' ? 'success' : 'secondary';
  };


  const getRecurrenceBadgeVariant = (type: RecurrenceType): string => {
    switch (type) {
      case 'specific_date':
        return 'partner';
      case 'weekly':
        return 'partner';
      case 'daily':
        return 'internal';
      case 'custom_range':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAssignedDriversCount = (template: ShiftTemplate): number => {
    return template.courierIds?.length || 0;
  };

  // Templates are already paginated server-side, so use them directly
  const paginatedTemplates = displayedTemplates;

  const totalPages = externalTotalPages ?? Math.ceil((externalTotalTemplates || displayedTemplates.length) / ITEMS_PER_PAGE);
  const totalTemplates = externalTotalTemplates ?? displayedTemplates.length;

  const effectiveTotalTemplates = totalTemplates;
  const effectiveTotalPages = Math.ceil((totalTemplates || 0) / ITEMS_PER_PAGE);

  React.useEffect(() => {
    if (page >= totalPages && totalPages > 0) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, page, setPage]);

  return (
    <div className="main-wrapper">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="pageTitle mb-1 text-xl font-bold">Shift Scheduler</h2>
          <p className="text-sm text-gray-500">
            Create and manage recurring shift schedules for automatic generation
          </p>
        </div>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={onCreateTemplate}
          additionalStyles="!flex-none text-sm py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 !min-w-0 px-4"
        >
          <div className="flex items-center justify-center gap-2 whitespace-nowrap">
            <AddIcon fontSize="small" />
            <span>Create Shift Schedule</span>
          </div>
        </CustomButton>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        {loading ? (
          <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm px-4 py-3 flex items-center gap-3 min-w-[140px]">
            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '8px' }} />
            <div className="flex-1">
              <Skeleton variant="text" width={80} height={14} />
              <Skeleton variant="text" width={50} height={24} className="mt-1" />
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
              <ScheduleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Shift Schedules</p>
              <p className="text-xl font-bold text-gray-900">{totalTemplates}</p>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-gray-900">All Shift Schedules</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Create and manage recurring shift schedules for automatic generation
            </p>
          </div>
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-inner" role="group">
            <button
              type="button"
              onClick={() => setViewMode('card')}
              className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'card'
                ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              title="Card View"
              aria-pressed={viewMode === 'card'}
            >
              <GridViewIcon fontSize="small" />
              <span className="hidden sm:inline">Card</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'table'
                ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              title="Table View"
              aria-pressed={viewMode === 'table'}
            >
              <ListViewIcon fontSize="small" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm p-5 mb-6">
          <div className="flex items-center flex-wrap gap-4">
            <div className="flex items-center gap-2 mr-2">
              <FilterIcon sx={{ fontSize: '18px', color: '#6B7280' }} />
              <span className="text-sm font-semibold text-gray-700">Filters:</span>
              <Tooltip
                title="Use filters to narrow down shift schedules by status, recurrence type, city, or search term."
                arrow
                placement="top"
              >
                <HelpIcon sx={{ fontSize: '16px', color: '#9CA3AF', cursor: 'help' }} />
              </Tooltip>
            </div>

            <Tooltip title="Search shift schedules" arrow placement="top">
              <div className="relative">
                <SearchIcon sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#9CA3AF' }} />
                <input
                  type="text"
                  value={debouncedSearch}
                  onChange={(e) => setDebouncedSearch(e.target.value)}
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none w-40 bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                />
              </div>
            </Tooltip>

            <Tooltip title="Filter shift schedules by status" arrow placement="top">
              <Autocomplete
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={filters.status ? { value: filters.status, label: filters.status === 'active' ? 'Active' : 'Inactive' } : null}
                onChange={(event, newValue) => {
                  handleFilterChange('status', newValue ? newValue.value : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Status"
                    size="small"
                    sx={{
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.label}</span>
                  </li>
                )}
                noOptionsText="No status found"
              />
            </Tooltip>

            <Tooltip title="Filter shift schedules by recurrence type" arrow placement="top">
              <Autocomplete
                options={[
                  { value: 'daily', label: 'Daily' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'specific_date', label: 'Specific Date' },
                  { value: 'custom_range', label: 'Custom Range' },
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={filters.recurrenceType ? { value: filters.recurrenceType, label: filters.recurrenceType === 'daily' ? 'Daily' : filters.recurrenceType === 'weekly' ? 'Weekly' : filters.recurrenceType === 'specific_date' ? 'Specific Date' : 'Custom Range' } : null}
                onChange={(event, newValue) => {
                  handleFilterChange('recurrenceType', newValue ? newValue.value : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Types"
                    size="small"
                    sx={{
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.label}</span>
                  </li>
                )}
                noOptionsText="No type found"
              />
            </Tooltip>

            <Tooltip title="Filter shift schedules by city" arrow placement="top">
              <Autocomplete
                options={cityOptions}
                getOptionLabel={(option) => option.name || ''}
                value={filters.cityId ? cityOptions.find(city => city.id === filters.cityId) || null : null}
                onChange={(event, newValue) => {
                  handleFilterChange('cityId', newValue ? newValue.id : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Cities"
                    size="small"
                    sx={{
                      minWidth: 180,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.name}</span>
                  </li>
                )}
                noOptionsText="No cities found"
              />
            </Tooltip>

            {filters.status && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '16px' }} />
                <span>{filters.status}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('status', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.recurrenceType && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '16px' }} />
                <span>{filters.recurrenceType === 'daily' ? 'Daily' : filters.recurrenceType === 'weekly' ? 'Weekly' : filters.recurrenceType === 'specific_date' ? 'Specific Date' : 'Custom Range'}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('recurrenceType', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.cityId && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <LocationIcon sx={{ fontSize: '16px' }} />
                <span>{getCityName(filters.cityId)}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('cityId', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.search && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <SearchIcon sx={{ fontSize: '16px' }} />
                <span>{filters.search}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('search', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {hasActiveFilters && (
              <Tooltip title="Remove all active filters" arrow placement="top">
                <button
                  onClick={clearAllFilters}
                  className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
                >
                  Clear All
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20">
                <CircularProgress size={48} sx={{ color: '#3B82F6', mb: 2 }} />
                <p className="text-sm font-medium text-gray-600">Loading</p>
              </div>
            ) : displayedTemplates.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
                <ScheduleIcon sx={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '8px' }} />
                <p className="text-sm font-medium text-gray-600">No shift schedules found</p>
                <p className="text-xs text-gray-400 mt-1">Click "Create Shift Schedule" to get started</p>
              </div>
            ) : (
              paginatedTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onViewTemplate={onViewTemplate}
                  onEditTemplate={onEditTemplate}
                  onDeleteTemplate={onDeleteTemplate}
                  onToggleTemplate={onToggleTemplate}
                  onGenerateShifts={onGenerateShifts}
                  getCityName={getCityName}
                  getNeighborhoodName={getNeighborhoodName}
                  getRecurrenceLabel={getRecurrenceLabel}
                  getAssignedDriversCount={getAssignedDriversCount}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  getRecurrenceBadgeVariant={getRecurrenceBadgeVariant}
                />
              ))
            )}
          </div>
        ) : (
          <TableContainer className="overflow-x-auto w-full" sx={{ overflowX: 'auto', overflowY: 'visible' }}>
            <Table
              sx={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0 10px',
                '& thead th': {
                  borderBottom: 'none',
                  fontSize: '12px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  backgroundColor: 'rgba(249, 250, 251, 0.8)',
                },
              }}
              aria-label="templates table"
            >
              <TableHead>
                <TableRow className='bg-gray-400 rounded-lg'>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '15%', minWidth: '150px' }}>Shift Schedule Name</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '12%', minWidth: '120px' }}>Recurrence</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '100px' }}>Time</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '7%', minWidth: '80px' }}>Drivers</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '6%', minWidth: '70px' }}>Priority</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '90px' }}>City</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '100px' }}>Neighborhood</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '100px' }}>Status</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '100px' }}>Created</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" sx={{ width: '8%', minWidth: '100px' }}>Updated</TableCell>
                  <TableCell className="font-semibold text-gray-600 py-2 tracking-wide" align="right" sx={{ width: '12%', minWidth: '150px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" className="py-20">
                      <div className="flex flex-col items-center justify-center">
                        <CircularProgress size={48} sx={{ color: '#3B82F6', mb: 2 }} />
                        <p className="text-sm font-medium text-gray-600">Loading</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : displayedTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" className="py-12">
                      <div className="text-gray-500">
                        <p className="text-sm font-medium">No shift schedules found</p>
                        <p className="text-xs text-gray-400 mt-1">Create a new shift schedule to get started</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTemplates.map((template, index) => {
                    return (
                      <TableRow
                        key={template.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '& td, & th': { borderBottom: 'none' },
                          '& td:first-of-type, & th:first-of-type': {
                            borderTopLeftRadius: 12,
                            borderBottomLeftRadius: 12,
                          },
                          '& td:last-of-type, & th:last-of-type': {
                            borderTopRightRadius: 12,
                            borderBottomRightRadius: 12,
                          },
                          '&:hover': {
                            background: 'linear-gradient(90deg, rgba(75, 85, 99, 0.15) 0%, rgba(75, 85, 99, 0.08) 100%)',
                            boxShadow: '0 4px 16px rgba(75, 85, 99, 0.25)',
                            '& td:first-of-type, & th:first-of-type': {
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                            '& td:last-of-type, & th:last-of-type': {
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                          },
                          position: 'relative',
                          transition: 'all 0.2s ease-in-out',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 24,
                            right: 24,
                            bottom: -5,
                            height: '1px',
                            backgroundColor: 'rgba(226, 232, 240, 0.9)',
                          }
                        }}
                        className="bg-white border border-gray-100"
                      >
                        <TableCell component="th" scope="row" className="py-2.5" sx={{ overflow: 'hidden' }}>
                          <div className="flex flex-col leading-tight min-w-0">
                            <span className="text-[11px] font-mono text-gray-500 uppercase tracking-wide">
                              #{template.id}
                            </span>
                            <span className="font-semibold text-sm text-gray-900 mt-0.5 truncate block min-w-0">
                              {template.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex justify-start">
                              <Badge variant={getRecurrenceBadgeVariant(template.recurrenceType) as any} className="text-[11px] font-semibold px-2 py-0.5">
                                {getRecurrenceLabel(template.recurrenceType, template)}
                              </Badge>
                            </div>

                            {template.recurrenceType === 'specific_date' && template.specificDate && (
                              <div className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
                                <div className="text-xs font-medium text-gray-700 mb-0.5">Date:</div>
                                <div className="text-xs text-gray-600">{template.specificDate}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{formatDate(template.specificDate)}</div>
                              </div>
                            )}

                            {template.recurrenceType === 'weekly' && template.daysOfWeekNames && template.daysOfWeekNames.length > 0 && (
                              <div className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
                                <div className="text-xs font-medium text-gray-700 mb-0.5">Days:</div>
                                <div className="text-xs text-gray-600">{template.daysOfWeekNames.join(', ')}</div>
                              </div>
                            )}

                            {template.recurrenceType === 'custom_range' && (template.validFrom || template.validUntil) && (
                              <div className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
                                <div className="text-xs font-medium text-gray-700 mb-0.5">Date Range:</div>
                                <div className="text-xs text-gray-600">
                                  {template.validFrom && template.validUntil
                                    ? `${template.validFrom} to ${template.validUntil}`
                                    : template.validFrom
                                      ? `From: ${template.validFrom}`
                                      : `Until: ${template.validUntil}`
                                  }
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {template.validFrom && (
                                    <span>From: {formatDate(template.validFrom)}</span>
                                  )}
                                  {template.validFrom && template.validUntil && <span className="mx-1">•</span>}
                                  {template.validUntil && (
                                    <span>Until: {formatDate(template.validUntil)}</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-sm text-gray-800 font-medium">
                            {formatTime(template.startTime)} - {formatTime(template.endTime)}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className={`text-sm font-medium ${getAssignedDriversCount(template) >= template.requiredDrivers
                            ? 'text-green-700'
                            : getAssignedDriversCount(template) >= template.requiredDrivers * 0.8
                              ? 'text-amber-700'
                              : 'text-red-700'
                            }`}>
                            {getAssignedDriversCount(template)} / {template.requiredDrivers}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${(template.priority || 0) >= 8
                            ? 'text-red-600 bg-red-50'
                            : (template.priority || 0) >= 5
                              ? 'text-amber-600 bg-amber-50'
                              : (template.priority || 0) >= 3
                                ? 'text-blue-600 bg-blue-50'
                                : 'text-gray-600 bg-gray-50'
                            }`}>
                            {template.priority || 0}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-sm text-gray-800 font-medium">
                            {template.cityId ? (cityOptions.find(c => String(c.id) === String(template.cityId))?.name || template.cityId) : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-sm text-gray-800 font-medium">
                            {template.neighborhoodId ? getCityName(template.neighborhoodId) : (template.zone || 'N/A')}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Badge variant={getStatusBadgeVariant(template.status)}>
                            {template.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-sm text-gray-800 font-medium">
                            {template.createdAt ? formatDateTime(template.createdAt) : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-sm text-gray-800 font-medium">
                            {template.updatedAt ? formatDateTime(template.updatedAt) : 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="py-4 px-6" align="right">
                          <div className="flex items-center gap-1 justify-end">
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                onClick={() => onViewTemplate(template.id)}
                                className="hover:bg-primaryColor/10"
                              >
                                <VisibilityIcon sx={{ fontSize: '18px', color: '#6B7280' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Shift Schedule" arrow>
                              <IconButton
                                size="small"
                                onClick={() => onEditTemplate(template.id)}
                                className="hover:bg-blue-100"
                              >
                                <EditIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={template.status === 'active' ? 'Deactivate Shift Schedule' : 'Activate Shift Schedule'} arrow>
                              <div className="flex items-center">
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={template.status === 'active'}
                                      onChange={() => onToggleTemplate(template.id)}
                                      color="primary"
                                      size="small"
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#10B981',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#10B981',
                                        },
                                      }}
                                    />
                                  }
                                  label=""
                                  sx={{ margin: 0 }}
                                />
                              </div>
                            </Tooltip>
                            {/* Temporarily hidden: Generate Shifts */}
                            {/* <Tooltip title="Generate Shifts" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onGenerateShifts(template.id)}
                            className="hover:bg-purple-100"
                            disabled={template.status === 'inactive'}
                            sx={{
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <PlayIcon sx={{ fontSize: '18px', color: template.status === 'inactive' ? '#9CA3AF' : '#8B5CF6' }} />
                          </IconButton>
                        </Tooltip> */}
                            <Tooltip title="Delete Shift Schedule" arrow>
                              <IconButton
                                size="small"
                                onClick={() => onDeleteTemplate(template.id)}
                                className="hover:bg-red-100"
                              >
                                <DeleteIcon sx={{ fontSize: '18px', color: '#EF4444' }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {effectiveTotalTemplates > 0 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {page * ITEMS_PER_PAGE + 1} to{' '}
              {Math.min((page + 1) * ITEMS_PER_PAGE, effectiveTotalTemplates)} of{' '}
              {effectiveTotalTemplates} shift schedule{effectiveTotalTemplates !== 1 ? 's' : ''}
              {hasActiveFilters && ' (filtered)'}
            </div>
            {effectiveTotalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === 0
                    ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  Previous
                </button>
                {(() => {
                  const pagesToShow = [];
                  const maxPagesToShow = 5;
                  let startPage = Math.max(0, page - Math.floor(maxPagesToShow / 2));
                  let endPage = Math.min(effectiveTotalPages - 1, startPage + maxPagesToShow - 1);

                  if (endPage - startPage < maxPagesToShow - 1) {
                    startPage = Math.max(0, endPage - maxPagesToShow + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pagesToShow.push(i);
                  }

                  return pagesToShow.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === pageNum
                        ? 'bg-primaryColor text-white border border-primaryColor'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => setPage(Math.min(effectiveTotalPages - 1, page + 1))}
                  disabled={page >= effectiveTotalPages - 1}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page >= effectiveTotalPages - 1
                    ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

